import React from "react";
import { ChevronLeft, Calendar, User } from "lucide-react";
import { BlogPost } from "../types";
import { ProductImage } from "./ProductCard";

interface BlogDetailPageProps {
  post: BlogPost;
  onBack: () => void;
  trackEvent: (name: string, params?: Record<string, any>) => void;
}

const BlogDetailPage: React.FC<BlogDetailPageProps> = ({
  post,
  onBack,
  trackEvent,
}) => {
  if (!post) return null; // Safety check

  // Ensure content is a string
  const safeContent = typeof post.content === "string" ? post.content : "";

  return (
    <div className="bg-white min-h-screen pt-14 pb-24 font-sans text-slate-900">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <button
          onClick={onBack}
          className="group flex items-center text-sm font-bold text-slate-500 hover:text-slate-900 mb-8 transition-colors"
        >
          <ChevronLeft
            size={16}
            className="mr-1 group-hover:-translate-x-1 transition-transform"
          />{" "}
          Back to Home
        </button>

        <div className="mb-8">
          <div className="flex gap-4 text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
            <span className="flex items-center gap-1">
              <Calendar size={12} /> {post.date}
            </span>
            <span className="flex items-center gap-1">
              <User size={12} /> {post.author}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-6">
            {post.title}
          </h1>
        </div>

        <div className="w-full h-64 md:h-96 bg-slate-100 rounded-xl overflow-hidden mb-10 shadow-sm">
          <ProductImage
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Medium-like Typography and Spacing with Explicit List Styles */}
        <div
          className="prose prose-slate max-w-none prose-lg 
            prose-headings:font-bold prose-headings:text-slate-900 prose-headings:mt-12 prose-headings:mb-6
            prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-6 prose-p:text-lg
            prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline
            prose-blockquote:border-l-4 prose-blockquote:border-orange-500 prose-blockquote:pl-6 prose-blockquote:py-2 prose-blockquote:italic prose-blockquote:bg-slate-50 prose-blockquote:rounded-r-lg
            
            /* Explicitly enforce list styles using arbitrary variants to override any resets */
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 
            [&_li]:mb-2 

            prose-img:rounded-xl prose-img:shadow-md prose-img:my-10 prose-img:w-full
            prose-strong:text-slate-900 prose-strong:font-bold
            [&>figure]:my-10 [&>figure]:w-full [&>figure>img]:rounded-xl [&>figure>figcaption]:text-center [&>figure>figcaption]:text-sm [&>figure>figcaption]:text-slate-500 [&>figure>figcaption]:mt-3
            /* Hide the first image in content if it matches the main hero image to avoid duplication */
            [&>figure:first-of-type]:hidden [&>p>img:first-of-type]:hidden"
          dangerouslySetInnerHTML={{
            __html: safeContent || "<p>Content unavailable.</p>",
          }}
        />

        <div className="mt-16 pt-10 border-t border-slate-100 flex flex-col items-center text-center bg-slate-50 rounded-2xl p-10 shadow-sm">
          <h3 className="text-2xl font-bold text-slate-900 mb-3">
            Want to find the right routine for your baby?
          </h3>
          <p className="text-slate-600 mb-8 text-base">
            Take our 30-second quiz to get personalized recommendations.
          </p>
          <button
            onClick={() => {
              // Track Conversion from Blog to Quiz
              trackEvent("blog_conversion", {
                post_title: post.title,
                trigger: "bottom_cta",
              });
              onBack(); // This goes to Landing -> Start Quiz
            }}
            className="px-10 py-4 bg-slate-900 text-white font-bold rounded-xl shadow-lg hover:bg-slate-800 transition-all hover:scale-105"
          >
            Start Skin Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailPage;
